import { Link } from "react-router-dom";
import { ArrowRight, ImageIcon, Clapperboard, Users, Building2, Workflow, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const STEPS = [
    { num: "01", title: "Create", text: "Enter your creative idea or prompt." },
    { num: "02", title: "Generate", text: "Lyanka AI processes the request and generates AI-powered visual content." },
    { num: "03", title: "Refine", text: "Experiment with different creative directions and outputs." },
    { num: "04", title: "Download & Use", text: "Use generated content for personal, creative, marketing or professional projects, subject to the platform's terms and applicable model/content restrictions." },
];

const CAPABILITIES = [
    { icon: ImageIcon, title: "AI Image Generation", text: "Turn text and creative concepts into visually compelling AI-generated images." },
    { icon: Clapperboard, title: "AI Video Generation", text: "Transform ideas into AI-powered video concepts and visual sequences." },
    { icon: Users, title: "For Creators", text: "Designed for creators, filmmakers, editors, marketers, designers and storytellers." },
    { icon: Building2, title: "For Businesses", text: "Create marketing visuals, promotional concepts, social media content and creative assets." },
    { icon: Workflow, title: "Easy Workflow", text: "From idea to generated visual content through a streamlined creative workflow." },
];

const BENEFITS = [
    "Faster creative experimentation",
    "AI-assisted image and video creation",
    "Useful for content production",
    "Useful for marketing campaigns",
    "Useful for concept development",
    "Useful for filmmakers and pre-production",
    "Useful for social media content",
    "Useful for digital storytelling",
    "Accessible through a modern web platform",
    "Subscription-based access to premium capabilities",
];

const FAQS = [
    {
        q: "What is Lyanka AI?",
        a: "Lyanka AI is an AI-powered creative platform by ANA INTERNATIONAL PRIVATE LIMITED for generating images, videos and other visual content from creative ideas and prompts.",
    },
    {
        q: "Who is Lyanka AI for?",
        a: "It is designed for AI creators, filmmakers, video editors, content creators, social media creators, designers, marketing professionals, digital artists, small businesses, creative agencies and storytellers.",
    },
    {
        q: "How does Lyanka AI work?",
        a: "You enter a creative idea or prompt, Lyanka AI generates AI-powered visual content, you refine the results across different creative directions, and then download and use the output subject to the platform's terms.",
    },
    {
        q: "How is Lyanka AI priced?",
        a: "Lyanka AI operates on a subscription-based model. Subscribers access premium platform capabilities and allocated usage according to their selected plan. Plan details will be published with the platform launch.",
    },
];

const FAQ_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const SOFTWARE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Lyanka AI",
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    description: "Lyanka AI is an AI-powered creative platform for generating images and videos. Create visual content for filmmaking, social media, marketing, storytelling and creative projects.",
    url: "https://anainternationalpvt.com/lyanka-ai",
    creator: { "@type": "Organization", name: "ANA INTERNATIONAL PRIVATE LIMITED" },
};

const FaqItem = ({ q, a, i }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="card-line rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
            <button
                className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left"
                onClick={() => setOpen(!open)}
                data-testid={`faq-toggle-${i}`}
            >
                <span className="font-display font-semibold text-base text-[#f5f4f0]">{q}</span>
                <ChevronDown size={18} className={`text-[#c9a227] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <p className="px-7 pb-6 text-sm text-[#a3a29b] leading-relaxed">{a}</p>}
        </div>
    );
};

export default function Lyanka() {
    return (
        <div data-testid="lyanka-page">
            <SEO
                title="Lyanka AI | AI Image & Video Generation Platform"
                description="Lyanka AI is an AI-powered creative platform for generating images and videos. Create visual content for filmmaking, social media, marketing, storytelling and creative projects."
                path="/lyanka-ai"
                jsonLd={[SOFTWARE_SCHEMA, FAQ_SCHEMA]}
            />

            {/* HERO */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 pb-16">
                <div className="absolute inset-0 grid-bg" />
                <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] glow-gold rounded-full" />
                <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
                    <Reveal>
                        <div className="flex justify-center mb-10">
                            <div className="glass rounded-[28px] p-6 floaty">
                                <img src="/assets/lyanka-mark.png" alt="Lyanka AI — AI image and video generator" className="w-44 sm:w-56 rounded-2xl" />
                            </div>
                        </div>
                        <Eyebrow>AI Image & Video Creation Platform</Eyebrow>
                        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight" data-testid="lyanka-headline">
                            Create <span className="gold-text">Without Limits.</span>
                        </h1>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-7 max-w-2xl mx-auto">
                            Generate AI-powered images and videos from your ideas with Lyanka AI — powerful creative
                            tools brought together into one easy-to-use platform.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-10 justify-center">
                            <a href="#how-it-works" className="btn-gold" data-testid="lyanka-cta-explore">
                                Explore Lyanka AI <ArrowRight size={16} />
                            </a>
                            <Link to="/contact" className="btn-line" data-testid="lyanka-cta-contact">
                                Contact Us
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how-it-works" className="py-24 bg-[#0d0d0f] border-y border-white/[0.07]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>How It Works</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">From idea to visual in four steps</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14" data-testid="workflow-steps">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.num} delay={i * 0.08}>
                                <div className="card-line rounded-2xl p-8 h-full">
                                    <span className="font-display font-bold text-3xl gold-text">{s.num}</span>
                                    <h3 className="font-display font-semibold text-lg mt-5 uppercase tracking-wide">{s.title}</h3>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-3">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CAPABILITIES */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>Capabilities</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5 max-w-2xl leading-tight">
                            One platform for AI-powered visual creation
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14" data-testid="capabilities-grid">
                        {CAPABILITIES.map((c, i) => (
                            <Reveal key={c.title} delay={i * 0.07}>
                                <div className="card-line rounded-2xl p-8 h-full">
                                    <c.icon size={24} className="text-[#e8c766]" />
                                    <h3 className="font-display font-semibold text-lg mt-5">{c.title}</h3>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-3">{c.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TURN IDEAS INTO VISUALS */}
            <section className="py-24 bg-[#0d0d0f] border-y border-white/[0.07]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
                    <Reveal>
                        <Eyebrow>Why Lyanka AI</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5 leading-tight">
                            Turn Ideas Into <span className="gold-text">Visuals.</span>
                        </h2>
                        <p className="text-[#a3a29b] text-base leading-relaxed mt-6">
                            Traditional visual production can require significant time, technical expertise, software and
                            resources. Lyanka AI is designed to make AI-assisted visual creation more accessible by
                            providing creative generation tools through one digital platform — reducing the technical
                            complexity involved in AI-powered visual creation.
                        </p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="lyanka-benefits">
                        {BENEFITS.map((b, i) => (
                            <Reveal key={b} delay={i * 0.04}>
                                <div className="flex items-start gap-3 card-line rounded-xl px-5 py-4">
                                    <Check size={16} className="text-[#c9a227] mt-0.5 shrink-0" />
                                    <span className="text-sm text-[#d8d7d1]">{b}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SUBSCRIPTION MODEL */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute bottom-[-40%] right-[-10%] w-[600px] h-[600px] glow-gold rounded-full" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal>
                        <Eyebrow>Business Model</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">Built on a Subscription Model</h2>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-6 max-w-3xl">
                            Lyanka AI operates on a subscription-based digital business model. Users can subscribe to
                            access premium platform capabilities and allocated usage/credits according to their selected
                            plan. The subscription model allows ANA INTERNATIONAL PRIVATE LIMITED to continuously
                            invest in AI technology, product development, infrastructure, new creative tools, platform
                            improvements, user experience, and research and development.
                        </p>
                    </Reveal>
                    <div className="grid sm:grid-cols-3 gap-5 mt-14" data-testid="subscription-plans">
                        {["Plan 01", "Plan 02", "Plan 03"].map((p, i) => (
                            <Reveal key={p} delay={i * 0.08}>
                                <div className={`rounded-3xl p-9 h-full text-center ${i === 1 ? "border border-[#c9a227]/40 bg-gradient-to-b from-[#c9a227]/[0.08] to-transparent" : "card-line"}`}>
                                    <p className="mono-label">{p}</p>
                                    <p className="font-display font-semibold text-xl mt-5 text-[#f5f4f0]">To Be Announced</p>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-3">
                                        Subscription plan details and allocated usage will be published with the
                                        platform launch.
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="text-xs text-[#7c7b75] mt-8 font-mono tracking-wider">
                            PLAN STRUCTURE SUBJECT TO CHANGE PRIOR TO LAUNCH · CONTACT US FOR EARLY ACCESS UPDATES
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 bg-[#0d0d0f] border-t border-white/[0.07]">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>FAQ</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">Frequently asked questions</h2>
                    </Reveal>
                    <div className="space-y-4 mt-12" data-testid="faq-list">
                        {FAQS.map((f, i) => (
                            <FaqItem key={f.q} q={f.q} a={f.a} i={i} />
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <div className="text-center mt-14">
                            <Link to="/contact" className="btn-gold" data-testid="lyanka-faq-cta">
                                Explore Lyanka AI <ArrowRight size={16} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
